import { editorialTopicStatus } from '@/components/reach/editorial/EditorialPillarSheet';
import {
  type ArticleHeaderAction,
  articleHeaderAction,
  articleHeaderClauses,
} from '@/components/reach/editorial/editorial-article-header';
import { editorialNextStep } from '@/components/reach/editorial/editorial-next-step';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { useArticlePublicationStates } from '@/hooks/reach/useArticleHandoff';
import { useArticleTopicJob } from '@/hooks/reach/useArticleReviewJob';
import { useEditorialMap } from '@/hooks/reach/useEditorialMap';
import { useBrandKits } from '@/hooks/useBrandKits';
import {
  type ArticleReleaseInput,
  articleReviewState,
} from '@/lib/content-doctrine/article-release';

/**
 * The one status line in the article reader's header: the same word and tone the Editorial table
 * and the hand-off dialog show for this article, then "Version 3 · Checks passed · 2 notes open",
 * then the next step as the header's one primary button when the reader can carry it out.
 *
 * It does not derive anything of its own. The label comes from `editorialTopicStatus`, the review
 * state from `articleReviewState`, the next step from `editorialNextStep` and the clauses from
 * `editorial-article-header.ts`, so the header cannot say "Ready to hand off" while the table says
 * "Needs review".
 */
export function EditorialArticleStatus({
  topicId,
  release,
  onAction,
}: {
  topicId: string;
  release: ArticleReleaseInput;
  /** Carries out the next step. Left out, the header shows the status line without a button. */
  onAction?: (action: ArticleHeaderAction) => void;
}): JSX.Element | null {
  const { activeKit } = useBrandKits();
  const { data: map } = useEditorialMap();
  const job = useArticleTopicJob(activeKit?.id ?? null, topicId);
  const { data: publications } = useArticlePublicationStates([topicId]);
  const topic = [...(map?.allTopics ?? []), ...(map?.orphanedTopics ?? [])].find(
    (row) => row.id === topicId,
  );
  if (!topic) return null;

  const reviewState = articleReviewState(release);
  const status = editorialTopicStatus(
    { ...topic, hasArticle: true },
    job ?? undefined,
    { reviewState },
    publications?.get(topicId) ?? null,
  );
  const clauses = articleHeaderClauses({ release, revisions: job?.revisions });
  const action = onAction
    ? articleHeaderAction(
        editorialNextStep({
          statusId: status.id,
          hasArticle: true,
          score: { reviewState, webReadiness: release.webReadiness },
        }),
        release.unresolvedEditorialFindings,
      )
    : null;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <StatusBadge tone={status.tone} fill="outline" className="shrink-0">
        {status.label}
      </StatusBadge>
      {clauses.length ? (
        // One line that truncates, never wraps: the header is a single row at every width, and
        // below `md` the badge alone carries the state.
        <p className="hidden min-w-0 max-w-80 truncate whitespace-nowrap text-detail text-muted-foreground md:block">
          {clauses.map((clause, index) => (
            <span key={clause}>
              {index > 0 ? <span aria-hidden> · </span> : null}
              <span>{clause}</span>
            </span>
          ))}
        </p>
      ) : null}
      {action && onAction ? (
        <Button variant="neutral" size="sm" className="shrink-0" onClick={() => onAction(action)}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
